import { useLazyQuery, useReactiveVar } from '@apollo/client';
import CollectionsIcon from '@mui/icons-material/Collections';
import { Card, CardMedia, capitalize } from '@mui/material';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import { Link, useNavigate } from '@tanstack/react-router';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import {
  CAN_DELETE_SLUG,
  CAN_DOWNLOAD_SLUG,
  CAN_EDIT_SLUG,
  CAN_SHARE_SLUG,
  COLLECTION,
  GALLERY_SIZE,
  GALLERY_TYPE_ITEM_ACTION_MAP,
  HOME_PATH,
  LAYOUT,
  SEARCH_PARAM,
  TWOUP,
} from 'src/constants';
import { DownloadManager, queueAndDownload } from 'src/download';
import { buildDownloadLinksQ } from 'src/dynamoQueries/download';
import { disableContextMenuElem } from 'src/helpers';
import { useInitialNavParams } from 'src/helpers/filters';
import { useCustomToast } from 'src/hooks/menu';
import { onDownloadLinkErrorMsg } from 'src/messages/error';
import { akomiMediaCardGridVar } from 'src/store/akomiMediaCardGridVar';
import viewStateVar from 'src/store/viewState';
import { ActionPermsMenu } from '../AkomiMediaCard/MediaCardActionsIcon/ActionPermsMenu';
import { MediaCardPreviewModal } from '../AkomiMediaCard/MediaCardPreviewModal';
import styles from '../AkomiMediaCard/Styles';
import type { ActionOption, AkomiCollectionMediaCardProps } from '../types';
import AkomiCollectionMedia from './AkomiCollectionMedia';
import EmptyCollectionThumbnail from './EmptyCollectionThumbnail';
import GalleryCardMetadata from './GalleryCardMetadata';

interface DownloadLink {
  downloadPath: string;
  filename: string;
  globalId: string;
}

interface DownloadLinksData {
  downloadLinks: DownloadLink[];
}

const AkomiCollectionMediaCard: React.FC<AkomiCollectionMediaCardProps> = ({ collection, ...props }) => {
  /**AkomGalleryMediaCard
   * Different in a few ways, its a Gallery Card that represents the Gallery in a collection
   *  No Super View: Doesnt use the superView, uses the items fields, the item is a CollectionNode
   *  CollectionNode should have name, an owner, and date created
   *  No formats on the Gallery itself
   *  It also will have a nested items, we will use the first items thumbnail format for preview
   */
  const firstItem = collection?.items?.[0];
  const hasKnownItems = Array.isArray(collection?.items);
  const isEmptyCollection = hasKnownItems && (collection.items?.length ?? 0) === 0;
  const hasPreviewSource = Boolean(firstItem?.objectId || firstItem?.contentObject?.id);

  const customSearchURL = new URLSearchParams(window.location.search);
  const isOnTwoupView = customSearchURL.get(LAYOUT) === TWOUP;
  const { density } = useReactiveVar(akomiMediaCardGridVar);
  const classes = styles({ isOnTwoupView, density });
  const [showPreview, setShowPreview] = useState(false);
  const viewState = useReactiveVar(viewStateVar);
  const mediaCardRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();
  const searchTerm = customSearchURL.get(SEARCH_PARAM);
  const hasWords = customSearchURL.getAll('include').filter((a: string) => !a.includes(' '));
  const hasPhrase = customSearchURL
    .getAll('include')
    .filter((a: string) => a.includes(' '))
    .toString()
    .replace('+', ' ');

  const [getDownloadLinks] = useLazyQuery<DownloadLinksData>(buildDownloadLinksQ());
  const { toastr } = useCustomToast();

  const handleDownload = (): void => {
    getDownloadLinks({
      variables: {
        globalIds: collection?.items?.map(({ contentObject }) => contentObject?.id),
      },
    })
      .then(({ data }) => {
        const downloadLinks = data?.downloadLinks;
        const manager = new DownloadManager(6, true);
        downloadLinks && queueAndDownload({ downloadLinks, manager, toastr });
      })
      .catch(({ error }: { error?: { message?: string } }) =>
        toastr.error(onDownloadLinkErrorMsg(error?.message ?? '')),
      );
  };

  const collectionType = (collection.collectionType ?? '').toLowerCase().trim();

  const handleActionClick = (option: ActionOption): void => {
    switch (option.slug) {
      case CAN_DOWNLOAD_SLUG:
        handleDownload();
        break;
      case CAN_EDIT_SLUG: {
        // Navigate to gallery page and open the info panel in edit mode
        viewStateVar({
          ...viewStateVar(),
          isGalleryPanelOpen: true,
          isGalleryPanelEditing: true,
        });
        // Use buildNavParams for consistency with Link navigation (includes layout, sort, etc.)
        const editNavParams = buildNavParams(`${HOME_PATH}/${collectionType}/${collection.id}`);
        navigate({ to: editNavParams.to, search: editNavParams.search });
        break;
      }
      case CAN_SHARE_SLUG:
        viewStateVar({
          ...viewStateVar(),
          shareDialog: collection.id,
          shareContent: COLLECTION,
        });
        break;
      case CAN_DELETE_SLUG:
        viewStateVar({
          ...viewStateVar(),
          deleteDialog: collection.id,
          shareContent: COLLECTION,
        });
        break;
      default:
        alert(`Sorry. "${option.label}" not implemented for ${collectionType} items in this version of Akomi`);
    }
  };

  useEffect(() => {
    mediaCardRef?.current && disableContextMenuElem(mediaCardRef.current);
  }, []);

  // Use the new TanStack Router-compatible navigation params
  // This returns separate `to` (path) and `search` (query params) to avoid URL corruption
  const buildNavParams = useInitialNavParams(GALLERY_SIZE);
  const galleryNavParams = buildNavParams(`${HOME_PATH}/${collectionType}/${collection.id}`);

  return (
    <Card
      raised={true}
      className={classes.mainmediacardframe}
      data-open-edit-collection={viewState.openEditCollection}
      data-testid="library-card"
    >
      <CardMedia className={classes.cardMedia}>
        {isEmptyCollection ? (
          <EmptyCollectionThumbnail />
        ) : (
          <AkomiCollectionMedia
            itemObjectId={firstItem?.objectId}
            itemId={firstItem?.contentObject?.id}
            onImageClick={() => setShowPreview(true)}
            akomiMediaCardIndex={props.akomiMediaCardIndex}
          />
        )}
        {hasPreviewSource && showPreview && (
          <MediaCardPreviewModal itemID={collection.id} pagination={true} onClose={() => setShowPreview(false)} />
        )}
      </CardMedia>
      <ActionPermsMenu
        itemId={collection.id}
        permissionMap={GALLERY_TYPE_ITEM_ACTION_MAP}
        handleActionClick={handleActionClick}
      />
      <CardContent className={classes.cardContent}>
        {/* Use TanStack Router's Link with separate to and search props to avoid URL corruption */}
        <Link className={classes.cardTitle} to={galleryNavParams.to} search={galleryNavParams.search}>
          <div className={classes.galleryTitleContainer}>
            <Typography className={classes.galleryTitle}>{capitalize(collection.collectionType)}</Typography>
            <CollectionsIcon className={classes.galleryIcon} />
          </div>
        </Link>
        {/* All metadata fields controlled by GalleryCardMeta view configuration */}
        <GalleryCardMetadata
          collection={collection}
          galleryNavParams={galleryNavParams}
          searchHighlights={[searchTerm, hasPhrase, ...hasWords].filter(Boolean) as string[]}
          highlights={collection.highlights}
          highlightLabels={collection.highlightLabels}
        />
      </CardContent>
    </Card>
  );
};

export default AkomiCollectionMediaCard;
